import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cog,
  Database,
  Building,
  Users2,
  FileText,
  Users,
  UserCheck,
  Monitor,
  Shield,
  ArrowRight,
  Settings,
  BarChart3,
  Clock,
  CheckCircle
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const SystemLanding = () => {
  const navigate = useNavigate();

  const systemCards = [
    {
      id: 'settings',
      title: 'Settings',
      description: 'System configuration & user preferences management',
      icon: <Cog size={24} />,
      path: '/system/settings',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['System config', 'Preferences', 'Customization']
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'User accounts & permissions management',
      icon: <Users size={24} />,
      path: '/system/users',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['User accounts', 'Permissions', 'Access control']
    },
    {
      id: 'master-data',
      title: 'Master Data',
      description: 'Dropdown values & system configuration management',
      icon: <Database size={24} />,
      path: '/system/master-data',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Dropdown values', 'Configuration', 'Data management']
    },
    {
      id: 'vendor-management',
      title: 'Vendor Management',
      description: 'Vendor & supplier management system',
      icon: <Building size={24} />,
      path: '/vendors',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Vendor tracking', 'Supplier management', 'Relationships']
    },
    {
      id: 'customer-management',
      title: 'Customer Management',
      description: 'Customer & client management system',
      icon: <Users2 size={24} />,
      path: '/customers',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Customer tracking', 'Client management', 'Relationships']
    },
    {
      id: 'clause-library',
      title: 'Clause Library',
      description: 'Legal clauses with AI-powered features',
      icon: <FileText size={24} />,
      path: '/system/clause-library',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Legal clauses', 'AI features', 'Library management']
    },

    {
      id: 'role-management',
      title: 'Role Management',
      description: 'Roles & access control management',
      icon: <UserCheck size={24} />,
      path: '/system/roles',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Role definition', 'Access control', 'Permissions']
    },
    {
      id: 'system-monitoring',
      title: 'System Monitoring',
      description: 'System health & performance monitoring',
      icon: <Monitor size={24} />,
      path: '/system/monitoring',
      iconBg: '#D6EFFF',
      iconLine: 'var(--gold-primary)',
      features: ['Health monitoring', 'Performance tracking', 'Alerts']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="System Management"
        description="Complete system administration and configuration management"
      />
      <div className="page-container">
        {/* System Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {systemCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#D6EFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine || 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SystemLanding;
